// frontend/AgentLeads.jsx
// Admin page: leads created from the agent app (tbl_leads.agent_app = 1), agent wise.
// Create React App compatible. No extra dependencies. Styles are embedded below.
//
//   GET /agent/leads/summary?from=&to=
//   GET /agent/leads?user_id=&search=&status=&from=&to=&page=&limit=

import { useCallback, useEffect, useMemo, useState } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "/api/v1";
const PAGE_SIZE = 20;
const DEBUG = true; // set to false when everything works

/* ----------------------------- helpers ----------------------------- */

const log = (...a) =>
  DEBUG && console.log("%c[AgentLeads]", "color:#FF3D8B;font-weight:bold", ...a);

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

async function api(path) {
  const url = `${API_BASE}${path}`;
  log("→ GET", url);
  let res;
  try {
    res = await fetch(url, { headers: authHeaders() });
  } catch (e) {
    console.error("[AgentLeads] network/CORS error", url, e);
    throw new Error("Cannot reach the server (network or CORS error)");
  }
  const raw = await res.text();
  let json;
  try {
    json = JSON.parse(raw);
  } catch {
    console.error("[AgentLeads] non-JSON response", res.status, raw.slice(0, 200));
    throw new Error(`Server returned non-JSON (HTTP ${res.status})`);
  }
  log("←", res.status, json.debug || "");
  if (!res.ok || json.success === false) {
    throw new Error(json.debug || json.message || `Request failed (HTTP ${res.status})`);
  }
  return json;
}

const PALETTE = [
  { bg: "#FFE0EC", fg: "#E0246B" }, // pink
  { bg: "#E3E0FF", fg: "#4A35E0" }, // indigo
  { bg: "#D2F5EE", fg: "#0A8F7B" }, // teal
  { bg: "#FFEBCB", fg: "#C46A00" }, // orange
  { bg: "#DDF0FF", fg: "#1479C9" }, // sky
  { bg: "#EBE0FF", fg: "#8A2BE2" }, // purple
  { bg: "#E2F7D5", fg: "#3F8F10" }, // green
];
const colorFor = (id) => PALETTE[Math.abs(Number(id) || 0) % PALETTE.length];

const initials = (name, id) => {
  if (!name) return String(id).slice(-2);
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || "A";
};

const toDate = (s) => new Date(String(s).replace(" ", "T"));
const fmtDay = (s) =>
  s ? toDate(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtTime = (s) =>
  s ? toDate(s).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "";
const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—";

const dayLabel = (s) => {
  const d = toDate(s);
  const t = new Date();
  const y = new Date();
  y.setDate(t.getDate() - 1);
  if (d.toDateString() === t.toDateString()) return "Today";
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return fmtDay(s);
};

const leadState = (l) => {
  if (Number(l.is_convert) === 1) return { key: "converted", label: "Converted" };
  if (Number(l.close_sts) === 1) return { key: "closed", label: "Closed" };
  return { key: "open", label: "Open" };
};

const agentLabel = (a) => a.agent_name || `Agent #${a.agent_id}`;

/* ----------------------------- component ----------------------------- */

export default function AgentLeads() {
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [agentFilter, setAgentFilter] = useState("");

  const [leads, setLeads] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const loadAgents = useCallback(async () => {
    setAgentsLoading(true);
    try {
      const qs = new URLSearchParams();
      if (from) qs.set("from", from);
      if (to) qs.set("to", to);
      const json = await api(`/agent/leads/summary?${qs}`);
      setAgents(json.data || []);
    } catch (e) {
      console.error("[AgentLeads] loadAgents failed:", e);
      setError(e.message);
    } finally {
      setAgentsLoading(false);
    }
  }, [from, to]);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams({ page, limit: PAGE_SIZE, status });
      if (selected) qs.set("user_id", selected);
      if (search) qs.set("search", search);
      if (from) qs.set("from", from);
      if (to) qs.set("to", to);
      const json = await api(`/agent/leads?${qs}`);
      setLeads(json.data || []);
      setPagination(json.pagination || { page: 1, pages: 1, total: 0 });
    } catch (e) {
      console.error("[AgentLeads] loadLeads failed:", e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, status, selected, search, from, to]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);
  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const totals = useMemo(
    () =>
      agents.reduce(
        (t, a) => ({
          leads: t.leads + Number(a.total_leads),
          converted: t.converted + Number(a.converted),
          open: t.open + Number(a.open_leads),
          closed: t.closed + Number(a.closed),
          today: t.today + Number(a.today_leads),
        }),
        { leads: 0, converted: 0, open: 0, closed: 0, today: 0 }
      ),
    [agents]
  );

  const maxLeads = useMemo(
    () => Math.max(1, ...agents.map((a) => Number(a.total_leads))),
    [agents]
  );

  const selectedAgent = agents.find((a) => a.agent_id === selected);
  const view = selectedAgent
    ? {
        leads: Number(selectedAgent.total_leads),
        converted: Number(selectedAgent.converted),
        open: Number(selectedAgent.open_leads),
        closed: Number(selectedAgent.closed),
      }
    : totals;
  const rate = view.leads ? Math.round((view.converted / view.leads) * 100) : 0;

  const visibleAgents = agents.filter(
    (a) =>
      agentLabel(a).toLowerCase().includes(agentFilter.toLowerCase()) ||
      String(a.agent_id).includes(agentFilter) ||
      (a.company_name || "").toLowerCase().includes(agentFilter.toLowerCase())
  );

  const grouped = useMemo(() => {
    const map = new Map();
    leads.forEach((l) => {
      const k = l.created_date ? dayLabel(l.created_date) : "No date";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(l);
    });
    return [...map.entries()];
  }, [leads]);

  const selectAgent = (id) => {
    setSelected(id);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatus("all");
    setFrom("");
    setTo("");
    setPage(1);
  };

  const hasFilters = search || status !== "all" || from || to;
  const col = selectedAgent ? colorFor(selectedAgent.agent_id) : { bg: "#FFE0EC", fg: "#E0246B" };

  return (
    <div className="al">
      <style>{css}</style>

      {/* header */}
      <header className="al-head">
        <div className="al-head-text">
          <span className="al-eyebrow">Agent app</span>
          <h1>Agent leads</h1>
          <p>Leads created by agents from the app, grouped agent wise.</p>
        </div>
        <div className="al-stats">
          <Stat tone="pink" value={totals.leads} label="Total leads" />
          <Stat tone="violet" value={agents.length} label="Agents creating leads" />
          <Stat tone="teal" value={totals.converted} label="Converted" />
          <Stat tone="orange" value={totals.today} label="Created today" />
        </div>
      </header>

      <div className="al-body">
        {/* agent rail */}
        <aside className="al-rail" aria-label="Agents">
          <div className="al-rail-top">
            <input
              className="al-input"
              type="search"
              placeholder="Find an agent or company"
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              aria-label="Find an agent"
            />
          </div>
          <ul className="al-agent-list">
            <li>
              <button
                className={`al-agent ${selected === null ? "is-active" : ""}`}
                onClick={() => selectAgent(null)}
              >
                <span className="al-avatar all">All</span>
                <span className="al-agent-main">
                  <strong>All agents</strong>
                  <small>{totals.leads} leads</small>
                </span>
              </button>
            </li>

            {agentsLoading &&
              [1, 2, 3, 4].map((i) => (
                <li key={i} className="al-skel-row">
                  <span className="al-skel circle" />
                  <span className="al-skel line" />
                </li>
              ))}

            {!agentsLoading && visibleAgents.length === 0 && (
              <li className="al-rail-empty">
                {agents.length === 0 ? "No agent leads found." : `No agents match “${agentFilter}”.`}
              </li>
            )}

            {visibleAgents.map((a) => {
              const c = colorFor(a.agent_id);
              const pct = Math.round((Number(a.total_leads) / maxLeads) * 100);
              return (
                <li key={a.agent_id}>
                  <button
                    className={`al-agent ${selected === a.agent_id ? "is-active" : ""}`}
                    onClick={() => selectAgent(a.agent_id)}
                    style={{ "--agent-fg": c.fg, "--agent-bg": c.bg }}
                  >
                    <span className="al-avatar" style={{ background: c.bg, color: c.fg }}>
                      {initials(a.agent_name, a.agent_id)}
                    </span>
                    <span className="al-agent-main">
                      <strong>{agentLabel(a)}</strong>
                      <small>
                        {a.company_name ? `${a.company_name} · ` : ""}
                        {a.converted} converted
                      </small>
                      <span className="al-meter">
                        <span style={{ width: `${pct}%`, background: c.fg }} />
                      </span>
                    </span>
                    <span className="al-count" style={{ background: c.fg }}>
                      {a.total_leads}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* main */}
        <main className="al-main">
          <section className="al-banner" style={{ "--b-bg": col.bg, "--b-fg": col.fg }}>
            <span className="al-avatar lg">
              {selectedAgent ? initials(selectedAgent.agent_name, selectedAgent.agent_id) : "All"}
            </span>
            <div className="al-banner-text">
              <h2>{selectedAgent ? agentLabel(selectedAgent) : "All agents"}</h2>
              <p>
                {selectedAgent
                  ? `${selectedAgent.company_name ? selectedAgent.company_name + " · " : ""}Agent ID ${selectedAgent.agent_id} · Last lead ${fmtDay(selectedAgent.last_lead_at)}, ${fmtTime(selectedAgent.last_lead_at)}`
                  : "Showing the latest leads from every agent."}
              </p>
            </div>
            <div className="al-mini">
              <div><strong>{view.leads}</strong><span>Leads</span></div>
              <div><strong>{view.open}</strong><span>Open</span></div>
              <div><strong>{view.converted}</strong><span>Converted</span></div>
              <div><strong>{view.closed}</strong><span>Closed</span></div>
              <div><strong>{rate}%</strong><span>Conversion</span></div>
            </div>
          </section>

          <section className="al-filters">
            <div className="al-search">
              <input
                className="al-input"
                type="search"
                placeholder="Search name, mobile, email, lead no. or requirement"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Search leads"
              />
            </div>

            <div className="al-seg" role="tablist" aria-label="Lead status">
              {[
                ["all", "All"],
                ["open", "Open"],
                ["converted", "Converted"],
                ["closed", "Closed"],
              ].map(([v, l]) => (
                <button
                  key={v}
                  role="tab"
                  aria-selected={status === v}
                  className={status === v ? "is-on" : ""}
                  onClick={() => {
                    setStatus(v);
                    setPage(1);
                  }}
                >
                  {l}
                </button>
              ))}
            </div>

            <label className="al-date">
              <span>From</span>
              <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
            </label>
            <label className="al-date">
              <span>To</span>
              <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
            </label>

            {hasFilters && (
              <button className="al-btn link" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </section>

          {error && (
            <div className="al-alert" role="alert">
              <span>{error}</span>
              <button className="al-btn link" onClick={() => { loadAgents(); loadLeads(); }}>
                Try again
              </button>
            </div>
          )}

          <section className="al-list" aria-busy={loading}>
            {loading &&
              [1, 2, 3].map((i) => (
                <div key={i} className="al-card skeleton">
                  <span className="al-skel circle" />
                  <div className="al-skel-col">
                    <span className="al-skel line w40" />
                    <span className="al-skel line w80" />
                  </div>
                </div>
              ))}

            {!loading && !error && leads.length === 0 && (
              <div className="al-empty">
                <div className="al-empty-mark">0</div>
                <h3>{hasFilters ? "No leads match these filters" : "No leads yet"}</h3>
                <p>
                  {hasFilters
                    ? "Change or clear the filters to see more leads."
                    : "Leads created from the agent app will show up here."}
                </p>
                {hasFilters && (
                  <button className="al-btn solid" onClick={clearFilters}>
                    Clear filters
                  </button>
                )}
              </div>
            )}

            {!loading &&
              grouped.map(([label, items]) => (
                <div key={label} className="al-day">
                  <h3 className="al-day-label">
                    {label}
                    <em>{items.length}</em>
                  </h3>
                  {items.map((l) => {
                    const c = colorFor(l.agent_id);
                    const st = leadState(l);
                    return (
                      <article
                        key={l.lead_id}
                        className="al-card"
                        style={{ "--stripe": c.fg }}
                      >
                        <span className="al-avatar" style={{ background: c.bg, color: c.fg }}>
                          {initials(l.customer_name, l.lead_id)}
                        </span>

                        <div className="al-card-body">
                          <div className="al-card-top">
                            <strong className="al-cust">{l.customer_name || "Unnamed lead"}</strong>
                            <span className={`al-status ${st.key}`}>{st.label}</span>
                            <span className="al-time">{fmtTime(l.created_date)}</span>
                          </div>

                          <div className="al-contact">
                            {l.mob_no && <span>📞 {l.mob_no}</span>}
                            {l.email && <span>✉️ {l.email}</span>}
                          </div>

                          {l.requirements && <p className="al-text">{l.requirements}</p>}

                          <div className="al-card-foot">
                            {l.lead_refno && <span className="al-chip violet">{l.lead_refno}</span>}
                            <span className="al-chip muted">Lead #{l.lead_id}</span>
                            {(l.pick_date || l.drop_date) && (
                              <span className="al-chip teal">
                                {fmtDate(l.pick_date)} → {fmtDate(l.drop_date)}
                              </span>
                            )}
                            <span className="al-spacer" />
                            <button
                              className="al-by"
                              style={{ color: c.fg, background: c.bg }}
                              onClick={() => selectAgent(l.agent_id)}
                              title="Show only this agent"
                            >
                              {agentLabel({ agent_name: l.agent_name, agent_id: l.agent_id })}
                              {l.company_name ? ` · ${l.company_name}` : ""}
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ))}
          </section>

          {pagination.pages > 1 && (
            <nav className="al-pager" aria-label="Pagination">
              <span>
                Page {pagination.page} of {pagination.pages} · {pagination.total} leads
              </span>
              <div>
                <button className="al-btn ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </button>
                <button
                  className="al-btn solid"
                  disabled={page >= pagination.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </nav>
          )}
        </main>
      </div>
    </div>
  );
}

function Stat({ tone, value, label }) {
  return (
    <div className={`al-stat ${tone}`}>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

/* ----------------------------- styles ----------------------------- */

const css = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.al {
  --ink: #1F1637;
  --ink-2: #5E5778;
  --line: #ECE6F5;
  --bg: #F8F4FF;
  --card: #FFFFFF;
  --pink: #E0246B;
  --violet: #6B3FE0;
  --violet-d: #5128C4;
  font-family: 'Plus Jakarta Sans', system-ui, -apple-system, 'Segoe UI', sans-serif;
  color: var(--ink);
  background: var(--bg);
  min-height: 100vh;
  font-size: 14px;
  line-height: 1.5;
}
.al *, .al *::before, .al *::after { box-sizing: border-box; }
.al h1, .al h2, .al h3, .al p { margin: 0; }
.al button { font-family: inherit; cursor: pointer; }
.al button:disabled { cursor: not-allowed; opacity: .5; }
.al :focus-visible { outline: 3px solid #C3A9FF; outline-offset: 2px; }

/* header */
.al-head {
  background: linear-gradient(120deg, #6B3FE0 0%, #B02BD8 55%, #FF3D8B 100%);
  color: #fff;
  padding: 28px 32px 60px;
  display: flex; flex-wrap: wrap; gap: 24px 40px;
  align-items: flex-end; justify-content: space-between;
}
.al-eyebrow { font-size: 12px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; opacity: .85; }
.al-head h1 { font-size: 32px; font-weight: 800; letter-spacing: -0.02em; margin-top: 2px; }
.al-head p { margin-top: 4px; color: #F1E6FF; max-width: 46ch; }
.al-stats { display: grid; grid-template-columns: repeat(4, minmax(120px, 1fr)); gap: 12px; flex: 1 1 540px; max-width: 720px; }
.al-stat { border-radius: 16px; padding: 14px 16px; display: flex; flex-direction: column; gap: 2px; color: #fff; box-shadow: 0 6px 18px rgba(30, 0, 80, .18); }
.al-stat strong { font-size: 28px; font-weight: 800; line-height: 1.1; }
.al-stat span { font-size: 12.5px; font-weight: 600; opacity: .95; }
.al-stat.pink { background: #E0246B; }
.al-stat.violet { background: #5B34D6; }
.al-stat.teal { background: #0A9A85; }
.al-stat.orange { background: #E27A00; }

/* layout */
.al-body {
  display: grid; grid-template-columns: 330px minmax(0, 1fr); gap: 20px;
  padding: 0 32px 40px; margin-top: -34px; align-items: start;
}

/* rail */
.al-rail {
  background: var(--card); border: 1px solid var(--line); border-radius: 18px;
  overflow: hidden; position: sticky; top: 16px; max-height: calc(100vh - 32px);
  display: flex; flex-direction: column; box-shadow: 0 6px 20px rgba(80, 40, 160, .08);
}
.al-rail-top { padding: 14px; border-bottom: 1px solid var(--line); }
.al-agent-list { list-style: none; margin: 0; padding: 8px; overflow-y: auto; }
.al-agent {
  width: 100%; display: flex; align-items: center; gap: 12px; padding: 10px;
  border: 0; background: transparent; border-radius: 14px; text-align: left; color: var(--ink);
  transition: background .15s;
}
.al-agent:hover { background: #F5F0FF; }
.al-agent.is-active { background: var(--agent-bg, #EDE4FF); box-shadow: inset 4px 0 0 var(--agent-fg, var(--violet)); }
.al-agent-main { display: flex; flex-direction: column; min-width: 0; flex: 1; gap: 1px; }
.al-agent-main strong { font-weight: 700; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.al-agent-main small { color: var(--ink-2); font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.al-meter { display: block; height: 5px; background: #F0EAFA; border-radius: 3px; margin-top: 5px; overflow: hidden; }
.al-meter span { display: block; height: 100%; border-radius: 3px; }
.al-count { color: #fff; font-weight: 800; font-size: 12.5px; min-width: 28px; height: 26px; padding: 0 8px; border-radius: 13px; display: grid; place-items: center; }
.al-rail-empty { padding: 16px; color: var(--ink-2); }

.al-avatar {
  width: 40px; height: 40px; flex: none; border-radius: 13px;
  display: grid; place-items: center; font-weight: 800; font-size: 14px;
}
.al-avatar.all { background: linear-gradient(135deg, #6B3FE0, #FF3D8B); color: #fff; }
.al-avatar.lg { width: 58px; height: 58px; border-radius: 18px; font-size: 19px; background: var(--b-fg); color: #fff; }

/* banner */
.al-banner {
  display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
  background: var(--b-bg); border-radius: 18px; padding: 18px 20px;
}
.al-banner-text { flex: 1 1 240px; min-width: 0; }
.al-banner h2 { font-size: 21px; font-weight: 800; color: var(--b-fg); letter-spacing: -0.01em; }
.al-banner p { color: var(--ink-2); font-size: 13px; margin-top: 2px; }
.al-mini { display: flex; gap: 8px; flex-wrap: wrap; }
.al-mini div { background: rgba(255,255,255,.75); border-radius: 12px; padding: 8px 14px; text-align: center; min-width: 76px; }
.al-mini strong { display: block; font-size: 19px; font-weight: 800; color: var(--b-fg); line-height: 1.2; }
.al-mini span { font-size: 11.5px; font-weight: 700; color: var(--ink-2); }

/* filters */
.al-filters {
  display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end;
  background: var(--card); border: 1px solid var(--line); border-radius: 18px;
  padding: 14px; margin-top: 14px;
}
.al-search { flex: 1 1 260px; }
.al-input, .al-date input {
  width: 100%; height: 40px; border: 1.5px solid var(--line); border-radius: 11px;
  padding: 0 12px; font: inherit; color: var(--ink); background: #fff;
}
.al-input:focus, .al-date input:focus { border-color: var(--violet); outline: none; box-shadow: 0 0 0 3px #E3D8FF; }
.al-date { display: flex; flex-direction: column; gap: 2px; font-size: 12px; font-weight: 600; color: var(--ink-2); }
.al-date input { width: 150px; }
.al-seg { display: inline-flex; background: #F1EBFB; border-radius: 11px; padding: 3px; height: 40px; }
.al-seg button { border: 0; background: transparent; padding: 0 13px; border-radius: 9px; font-weight: 700; color: var(--ink-2); }
.al-seg button.is-on { background: var(--violet); color: #fff; }

/* buttons */
.al-btn { height: 40px; padding: 0 16px; border-radius: 11px; font-weight: 700; font-size: 13.5px; border: 1.5px solid transparent; }
.al-btn.solid { background: var(--violet); color: #fff; }
.al-btn.solid:hover:not(:disabled) { background: var(--violet-d); }
.al-btn.ghost { background: #fff; color: var(--violet); border-color: var(--violet); }
.al-btn.ghost:hover:not(:disabled) { background: #F5F0FF; }
.al-btn.link { background: transparent; color: var(--violet); padding: 0 8px; }

.al-alert { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-top: 14px; padding: 12px 16px; border-radius: 12px; background: #FFE9EE; color: #A3173F; font-weight: 600; }

/* list */
.al-list { margin-top: 8px; }
.al-day { margin-top: 18px; }
.al-day-label { font-size: 13px; font-weight: 700; color: var(--ink-2); margin-bottom: 10px; display: flex; align-items: center; gap: 10px; }
.al-day-label em { font-style: normal; background: #EDE4FF; color: var(--violet); border-radius: 999px; padding: 1px 9px; font-size: 12px; }
.al-day-label::after { content: ""; flex: 1; height: 1px; background: var(--line); }

.al-card {
  display: flex; gap: 14px; background: var(--card); border: 1px solid var(--line);
  border-left: 5px solid var(--stripe, var(--line)); border-radius: 16px;
  padding: 16px; margin-bottom: 10px; box-shadow: 0 2px 8px rgba(80, 40, 160, .05);
}
.al-card-body { flex: 1; min-width: 0; }
.al-card-top { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 10px; }
.al-cust { font-weight: 800; font-size: 15.5px; }
.al-time { margin-left: auto; color: var(--ink-2); font-size: 12.5px; font-weight: 600; }
.al-status { font-size: 11.5px; font-weight: 800; padding: 2px 10px; border-radius: 999px; }
.al-status.open { background: #FFF0CC; color: #9A6700; }
.al-status.converted { background: #D2F5EE; color: #0A7F6C; }
.al-status.closed { background: #ECEAF2; color: #5E5778; }
.al-contact { display: flex; flex-wrap: wrap; gap: 4px 16px; margin-top: 4px; color: var(--ink-2); font-size: 13px; font-weight: 600; }
.al-text { margin-top: 8px; font-size: 14.5px; overflow-wrap: anywhere; white-space: pre-wrap; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.al-card-foot { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 12px; }
.al-spacer { flex: 1; }
.al-chip { font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 999px; }
.al-chip.violet { background: #EDE4FF; color: #5128C4; }
.al-chip.teal { background: #D2F5EE; color: #0A7F6C; }
.al-chip.muted { background: #F1EEF7; color: var(--ink-2); }
.al-by { border: 0; font-size: 12px; font-weight: 800; padding: 4px 12px; border-radius: 999px; max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.al-by:hover { filter: brightness(.95); text-decoration: underline; }

/* empty + skeleton */
.al-empty { text-align: center; padding: 56px 20px; background: var(--card); border: 1px dashed #D5C9EC; border-radius: 18px; margin-top: 18px; }
.al-empty-mark { width: 64px; height: 64px; margin: 0 auto 14px; border-radius: 20px; background: #FFE0EC; color: var(--pink); display: grid; place-items: center; font-size: 26px; font-weight: 800; }
.al-empty h3 { font-size: 18px; font-weight: 800; }
.al-empty p { color: var(--ink-2); margin: 6px auto 16px; max-width: 40ch; }

.al-skel { background: linear-gradient(90deg, #F0EAFA 25%, #FAF7FF 50%, #F0EAFA 75%); background-size: 200% 100%; animation: al-shimmer 1.3s infinite; border-radius: 8px; display: block; }
.al-skel.circle { width: 40px; height: 40px; border-radius: 13px; flex: none; }
.al-skel.line { height: 12px; }
.al-skel.w40 { width: 40%; } .al-skel.w80 { width: 80%; }
.al-skel-col { flex: 1; display: flex; flex-direction: column; gap: 10px; padding-top: 6px; }
.al-skel-row { display: flex; align-items: center; gap: 12px; padding: 10px; }
.al-skel-row .line { flex: 1; }
@keyframes al-shimmer { to { background-position: -200% 0; } }

/* pager */
.al-pager { display: flex; flex-wrap: wrap; gap: 12px; justify-content: space-between; align-items: center; margin-top: 18px; color: var(--ink-2); font-weight: 600; }
.al-pager div { display: flex; gap: 8px; }

/* responsive */
@media (max-width: 980px) {
  .al-head { padding: 24px 18px 56px; }
  .al-stats { grid-template-columns: repeat(2, 1fr); }
  .al-body { grid-template-columns: 1fr; padding: 0 18px 32px; }
  .al-rail { position: static; max-height: none; }
  .al-agent-list { display: flex; gap: 8px; overflow-x: auto; padding: 10px; }
  .al-agent { width: 250px; flex: none; border: 1px solid var(--line); }
  .al-skel-row, .al-rail-empty { flex: none; }
}
@media (max-width: 560px) {
  .al-date, .al-date input { width: 100%; }
  .al-date { flex: 1 1 140px; }
  .al-time { margin-left: 0; width: 100%; }
}
@media (prefers-reduced-motion: reduce) {
  .al-skel { animation: none; }
}
`;